<script setup lang="ts" generic="TData extends RowData">
import { mdiSelectionSearch } from "@mdi/js";
import {
	Button,
	Card,
	DataTableActiveFilters,
	DataTableContent,
	DataTableFilterMenu,
	DataTablePagination,
	DataTableRoot,
	DataTableRootEmits,
	DataTableRootProps,
	DataTableRow,
	DataTableSearch,
	FacetedOptions,
	Icon,
	RowData,
} from "@soft-db/ui";
import MobileHorizontalScrollHint from "./MobileHorizontalScrollHint.vue";

const { t } = useI18n();

const props = withDefaults(
	defineProps<
		{
			data: TData[];
			layout?: "page" | "contained" | "simple";
			filtersDef?: FacetedOptions<TData>;
			hidePagination?: boolean;
			defaultPageSize?: number;
			// eslint-disable-next-line vue/prop-name-casing
			"onClick:row"?: (row: DataTableRow<TData>) => void;
		} & /** @vue-ignore */ DataTableRootProps<TData>
	>(),
	{
		layout: "page",
	}
);

defineEmits</** @vue-ignore */ DataTableRootEmits>();

defineSlots<{
	/**
	 * Only if the layout is set to `page` and `simple` layout
	 */
	title(): void;
	action(): void;
	columns(): void;
}>();

const tableInstance = useTemplateRef("table");

defineExpose({
	instance: tableInstance,
});

const hasGlobalFilter = computed(() => {
	if (tableInstance.value == null) {
		return false;
	}

	const { globalFilter } = tableInstance.value.table.getState();
	return globalFilter != null && globalFilter !== "";
});

const hasColumnFilter = computed(() => {
	if (tableInstance.value == null) {
		return false;
	}

	const { columnFilters } = tableInstance.value.table.getState();
	return columnFilters.length !== 0;
});

const hasFilters = logicOr(hasGlobalFilter, hasColumnFilter);

function clearFilters() {
	if (tableInstance.value == null) {
		return;
	}

	tableInstance.value.table.setColumnFilters([]);
	tableInstance.value.table.setGlobalFilter("");
}

const attrs = useAttrs();

const pagination = computed(() => {
	if (props.defaultPageSize != null && !("pagination" in attrs)) {
		return { pageIndex: 0, pageSize: props.defaultPageSize };
	}
	return undefined;
});

const tableScrollContainer = useTemplateRef("$tableScrollContainer");

const showGradient = ref(true);
const scrolledOnce = ref(false);

const { arrivedState } = useScroll(tableScrollContainer, {
	onScroll: () => {
		scrolledOnce.value = true;
	},
	throttle: 100,
});

watchEffect(() => {
	if (!scrolledOnce.value) {
		return;
	}

	showGradient.value = !arrivedState.right;
});

const isPageLayout = computed(() => props.layout === "page");
const isContainedLayout = computed(() => props.layout === "contained");
const isSimpleLayout = computed(() => props.layout === "simple");

const slots = useSlots();

const hasTitleSlot = computed(() => "title" in slots);
const hasActionSlot = computed(() => "action" in slots);
</script>

<template>
	<DataTableRoot
		ref="table"
		:styled="false"
		:data="data"
		:pagination="pagination"
		v-bind="attrs as unknown as Omit<DataTableRootProps<TData>, 'data'>"
	>
		<div :class="['flex flex-col gap-2', isPageLayout && '*:mx-4']">
			<div v-if="isPageLayout" class="flex items-center justify-between">
				<!-- Only available on page layout -->
				<div>
					<slot name="title" />
				</div>
				<div>
					<slot name="action" />
				</div>
			</div>
			<Card>
				<div
					v-if="isSimpleLayout && (hasTitleSlot || hasActionSlot)"
					class="border-border/60 flex w-full items-center justify-between border-b p-4"
				>
					<div>
						<slot name="title" />
					</div>
					<div>
						<slot name="action" />
					</div>
				</div>

				<div v-else-if="!isSimpleLayout" class="border-border/60 flex flex-col gap-2 border-b p-4">
					<div class="flex w-full gap-2">
						<DataTableSearch class="max-w-80 grow" />
						<DataTableFilterMenu v-if="filtersDef != null" :options="filtersDef" countBadge />
						<div v-if="isContainedLayout" class="ml-auto">
							<slot name="action" />
						</div>
					</div>

					<DataTableActiveFilters v-if="filtersDef != null" :options="filtersDef" />
				</div>

				<div class="relative">
					<div ref="$tableScrollContainer" class="overflow-x-auto">
						<DataTableContent
							v-bind="{
								'onClick:row': props['onClick:row'],
							}"
						>
							<slot name="columns" />
							<template #empty>
								<div class="flex w-full items-center justify-center">
									<span v-if="data.length === 0"> {{ t("table.noData") }} </span>
									<div
										v-else-if="hasFilters"
										class="mt-1 flex flex-col items-center justify-center gap-2"
									>
										<Icon class="text-muted size-12" :d="mdiSelectionSearch"></Icon>
										<div>{{ t("table.noMatchingResult") }}</div>
										<Button
											class="h-fit"
											size="sm"
											variant="link"
											color="primary"
											@click="clearFilters"
										>
											{{ t("table.clearFilters") }}
										</Button>
									</div>
								</div>
							</template>
						</DataTableContent>
					</div>

					<MobileHorizontalScrollHint :content="tableScrollContainer" />
				</div>

				<div
					v-if="!hidePagination"
					class="border-border/60 flex w-full justify-center border-t p-4 md:justify-end"
				>
					<DataTablePagination class="flex-nowrap" />
				</div>
			</Card>
		</div>
	</DataTableRoot>
</template>
